import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { HDRLoader } from 'three/addons/loaders/HDRLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { EXPLOSION_SPRITES, LIGHTING, ROADS, STARS } from './SimConfig.js';
import { ALL_SOUNDS, audioUrl, trimToSource } from '../audio/soundManifest.js';

export default class AssetManager {
  /** @type {Map<string, THREE.Group>} */
  #models = new Map();
  /** @type {Map<string, THREE.Texture>} */
  #textures = new Map();
  /** @type {Map<string, THREE.DataTexture>} */
  #hdrs = new Map();
  /** @type {Map<string, AudioBuffer>} */
  #audioBuffers = new Map();
  /** @type {Map<string, ArrayBuffer>} */
  #binaries = new Map();
  /** @type {Map<string, object>} */
  #json = new Map();

  constructor(anisotropy = 1) {
    this.anisotropy = anisotropy;
    this.manager = new THREE.LoadingManager();
    this.gltfLoader = new GLTFLoader(this.manager);
    // The production build meshopt-compresses every GLB (tools/vite-plugin-
    // optimize-assets.mjs); the uncompressed dev sources load unaffected.
    this.gltfLoader.setMeshoptDecoder(MeshoptDecoder);
    this.hdrLoader = new HDRLoader(this.manager);
    this.textureLoader = new THREE.TextureLoader(this.manager);
    this.audioLoader = new THREE.AudioLoader(this.manager);
    this.binaryLoader = new THREE.FileLoader(this.manager).setResponseType('arraybuffer');
    this.jsonLoader = new THREE.FileLoader(this.manager).setResponseType('json');

    /** Optional progress callback: (loaded, total) => void */
    this.onProgress = null;

    this.manager.onProgress = (_url, loaded, total) => {
      this.onProgress?.(loaded, total);
    };
  }

  /**
   * Load all assets. Returns a promise that resolves when everything is ready.
   */
  async load() {
    const base = import.meta.env.BASE_URL + 'assets/';

    // Collect unique lighting asset filenames
    const hdrFiles = new Set();
    const bgFiles = new Set();
    for (const cfg of Object.values(LIGHTING)) {
      if (cfg.hdr) hdrFiles.add(cfg.hdr);
      if (cfg.background) bgFiles.add(cfg.background);
    }

    // Explosion sprite sheets — one per clip registered in SimConfig, loaded
    // and keyed by animation name so adding footage needs no change here.
    const explosionSheets = Object.entries(EXPLOSION_SPRITES.animations)
      .map(([name, cfg]) => ({ name, file: cfg.file }));

    // Audio buffers — decoded once and cached, then bound to THREE.Audio voices
    // by AudioManager per scenario. Loaded through the same LoadingManager so
    // each file counts toward the loading progress bar. Runs concurrently with
    // the model/texture/HDR loads below.
    const audioLoad = Promise.all(
      ALL_SOUNDS.map(s =>
        this.audioLoader.loadAsync(audioUrl(s.path))
          .then(buffer => this.#audioBuffers.set(s.name, trimToSource(s.path, buffer)))
      )
    );

    // Road graph — baked by tools/build-roads.mjs. Loaded here (rather than
    // lazily) because enemy spawning reads it synchronously at scenario launch,
    // so it has to be resolved before the loading screen releases. Non-fatal on
    // its own: a missing or unparseable graph just leaves `useRoads` spawns to
    // fall back to ordinary placement.
    const roadLoad = this.jsonLoader.loadAsync(base + ROADS.data)
      .then(data => this.#json.set(ROADS.data, data))
      .catch(err => console.warn(`AssetManager: road graph ${ROADS.data} unavailable`, err));

    const [gltf, buildingsGltf, airfieldGltf, mi8Gltf, mig21Gltf, btrGltf, tunguskaGltf, aim9Gltf, agm65Gltf, missileGltf, asphalt, particleAtlas, smokeTrail, shadow, rwrDisplay, starData, ...lightingResults] = await Promise.all([
      this.gltfLoader.loadAsync(base + 'models/a10.glb'),
      this.gltfLoader.loadAsync(base + 'models/buildings.glb'),
      this.gltfLoader.loadAsync(base + 'models/airfield.glb'),
      this.gltfLoader.loadAsync(base + 'models/mi8.glb'),
      this.gltfLoader.loadAsync(base + 'models/mig21.glb'),
      this.gltfLoader.loadAsync(base + 'models/btr.glb'),
      this.gltfLoader.loadAsync(base + 'models/2k22.glb'),
      this.gltfLoader.loadAsync(base + 'models/aim-9m.glb'),
      this.gltfLoader.loadAsync(base + 'models/agm65.glb'),
      this.gltfLoader.loadAsync(base + 'models/missile.glb'),
      this.textureLoader.loadAsync(base + 'textures/asphalt.jpg'),
      this.textureLoader.loadAsync(base + 'fx/particle_atlas.png'),
      this.textureLoader.loadAsync(base + 'fx/smoke_trail.jpg'),
      this.textureLoader.loadAsync(base + 'fx/shadow.png'),
      this.textureLoader.loadAsync(base + 'textures/rwr_display.png'),
      this.binaryLoader.loadAsync(base + STARS.data),
      ...[...hdrFiles].map(f => this.hdrLoader.loadAsync(base + f)),
      ...[...bgFiles].map(f => this.textureLoader.loadAsync(base + f)),
      ...explosionSheets.map(e => this.textureLoader.loadAsync(base + e.file)),
    ]);

    this.#models.set('a10', gltf.scene);
    this.#models.set('buildings', buildingsGltf.scene);
    this.#models.set('airfield', airfieldGltf.scene);
    this.#models.set('mi8', mi8Gltf.scene);
    this.#models.set('mig21', mig21Gltf.scene);
    this.#models.set('btr80', btrGltf.scene);
    this.#models.set('tunguska', tunguskaGltf.scene);
    this.#models.set('aim9', aim9Gltf.scene);
    this.#models.set('agm65', agm65Gltf.scene);
    this.#models.set('missile', missileGltf.scene);

    // Store HDR textures keyed by filename, and pre-bake hemisphere irradiance
    let idx = 0;
    for (const f of hdrFiles) {
      const hdr = lightingResults[idx++];
      hdr.mapping = THREE.EquirectangularReflectionMapping;
      hdr.userData.skyTint = computeSkyTint(hdr);
      this.#hdrs.set(f, hdr);
    }

    // Store background textures keyed by filename
    for (const f of bgFiles) {
      const bg = lightingResults[idx++];
      bg.mapping = THREE.EquirectangularReflectionMapping;
      bg.colorSpace = THREE.SRGBColorSpace;
      this.#textures.set(f, bg);
    }

    // Explosion sprite sheets — sRGB so the GPU decodes on sample and the
    // shader's fire thresholds work in linear light (the sheet is photographic
    // footage, unlike the particle atlas's greyscale masks). flipY off to match
    // the tile-row maths in explosion_sprite_vert.glsl.
    for (const e of explosionSheets) {
      const tex = lightingResults[idx++];
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.wrapS = THREE.ClampToEdgeWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;
      tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.flipY = false;
      this.#textures.set(e.name, tex);
    }

    // Asphalt — runway detail map. Used both as a multiplicative diffuse
    // overlay and as the runway material's bumpMap. sRGB so the colour
    // multiply happens in perceptually correct linear space; the bump
    // pipeline reads decoded values too but bumpScale absorbs the offset.
    asphalt.wrapS = THREE.RepeatWrapping;
    asphalt.wrapT = THREE.RepeatWrapping;
    asphalt.colorSpace = THREE.SRGBColorSpace;
    this.#textures.set('asphalt', asphalt);

    particleAtlas.minFilter = THREE.LinearMipmapLinearFilter;
    particleAtlas.magFilter = THREE.LinearFilter;
    particleAtlas.flipY = false;
    this.#textures.set('particleAtlas', particleAtlas);

    // Smoke trail strip — vertical seamless band, sampled with v repeating
    // along the trail and u clamped across the ribbon's width.
    smokeTrail.wrapS = THREE.ClampToEdgeWrapping;
    smokeTrail.wrapT = THREE.RepeatWrapping;
    smokeTrail.minFilter = THREE.LinearMipmapLinearFilter;
    smokeTrail.magFilter = THREE.LinearFilter;
    smokeTrail.flipY = false;
    this.#textures.set('smokeTrail', smokeTrail);

    // Aircraft ground shadow — alpha map sampled clamped so the plane's edges
    // fade to fully transparent rather than tiling.
    shadow.wrapS = THREE.ClampToEdgeWrapping;
    shadow.wrapT = THREE.ClampToEdgeWrapping;
    shadow.minFilter = THREE.LinearMipmapLinearFilter;
    shadow.magFilter = THREE.LinearFilter;
    this.#textures.set('shadow', shadow);

    // RWR scope artwork — rings + tick marks on transparent. Never sampled by
    // the GPU: RWRDisplay composites its `.image` onto the scope canvas.
    this.#textures.set('rwrDisplay', rwrDisplay);

    // Packed star catalogue — decoded into geometry by world/StarField.js.
    this.#binaries.set(STARS.data, starData);

    this.#applyAnisotropy();

    // Make sure audio buffers are decoded before resolving — concurrent with
    // the loads above, so this usually adds no wall-clock time.
    await Promise.all([audioLoad, roadLoad]);
  }

  /** Apply anisotropy to all loaded textures, including textures inside models. */
  #applyAnisotropy() {
    const a = this.anisotropy;

    for (const tex of this.#textures.values()) {
      tex.anisotropy = a;
    }

    for (const model of this.#models.values()) {
      model.traverse((child) => {
        if (!child.isMesh) return;
        const materials = Array.isArray(child.material)
          ? child.material
          : [child.material];
        for (const mat of materials) {
          for (const key of Object.keys(mat)) {
            const val = mat[key];
            if (val && val.isTexture) {
              val.anisotropy = a;
            }
          }
        }
      });
    }
  }

  /** Get a loaded GLTF model scene graph (original — clone it if adding to scene multiple times). */
  getModel(name) {
    return this.#models.get(name);
  }

  /** Get a loaded texture. */
  getTexture(name) {
    return this.#textures.get(name);
  }

  /** Get a loaded HDR environment texture. */
  getHDR(name) {
    return this.#hdrs.get(name);
  }

  /** Get a decoded, cached AudioBuffer by manifest name. */
  getAudioBuffer(name) {
    return this.#audioBuffers.get(name);
  }

  /** Get a loaded binary blob (packed data files) by path. */
  getBinary(name) {
    return this.#binaries.get(name);
  }

  /** Get a parsed JSON data file by path (undefined if its load failed). */
  getJSON(name) {
    return this.#json.get(name);
  }

  /** Get a named material from a loaded model. */
  getMaterial(modelName, materialName) {
    const model = this.#models.get(modelName);
    if (!model) return undefined;
    let found;
    model.traverse((child) => {
      if (found) return;
      if (child.isMesh && child.material?.name === materialName) {
        found = child.material;
      }
    });
    return found;
  }

  /** Get a named mesh from a loaded model. */
  getMesh(modelName, meshName) {
    const model = this.#models.get(modelName);
    if (!model) return undefined;
    let found;
    model.traverse((child) => {
      if (found) return;
      if (child.isMesh && child.name === meshName) {
        found = child;
      }
    });
    return found;
  }
}

// ── Sky tint baking ──────────────────────────────────────────────────────────

/**
 * Compute hemisphere-averaged sky color from an equirect HDR.
 * Returns { top: [r,g,b], bottom: [r,g,b] } in linear space.
 *
 * Pixel values are clamped before averaging — the sun in an HDR is often
 * 100×–1000× brighter than the surrounding sky, and including it raw makes
 * the hemisphere average dominated by a few hot pixels (smoke goes white).
 * The clamp filters that spike while preserving the overall sky tint.
 *
 * Solid-angle weighted by cos(latitude) so polar pixels (which represent
 * less area on the sphere) are not over-counted.
 */
function computeSkyTint(hdr) {
  const W = hdr.image.width;
  const H = hdr.image.height;
  const data = hdr.image.data; // Float32Array, RGBA

  // Downsample stride — 64×32 effective grid is plenty for a single average.
  const sx = Math.max(1, Math.floor(W / 64));
  const sy = Math.max(1, Math.floor(H / 32));

  // Anti sun-spike clamp — keeps sky/cloud values, drops disk values.
  const CLAMP = 4.0;

  let topR = 0, topG = 0, topB = 0, topW = 0;
  let botR = 0, botG = 0, botB = 0, botW = 0;

  for (let y = 0; y < H; y += sy) {
    // Equirect: row 0 is top of sphere, row H-1 is bottom.
    const lat = ((0.5 - (y + 0.5) / H)) * Math.PI;
    const ny = Math.sin(lat);
    const w = Math.cos(lat); // solid-angle weight
    for (let x = 0; x < W; x += sx) {
      const o = (y * W + x) * 4;
      const r = Math.min(data[o],     CLAMP);
      const g = Math.min(data[o + 1], CLAMP);
      const b = Math.min(data[o + 2], CLAMP);
      if (ny > 0) {
        topR += r * w; topG += g * w; topB += b * w; topW += w;
      } else {
        botR += r * w; botG += g * w; botB += b * w; botW += w;
      }
    }
  }

  const top = topW > 0
    ? [topR / topW, topG / topW, topB / topW]
    : [1, 1, 1];
  const bot = botW > 0
    ? [botR / botW, botG / botW, botB / botW]
    : [0.3, 0.3, 0.3];

  return { top, bottom: bot };
}
